import { useMemo, useRef } from 'react'
import { App, Select, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AgentRef } from '@/features/agent/AgentRef'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { branchLabel, setSubscribersBranch } from '@/features/branch/branchApi'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { useCanonicalPlanIndex, usePlanDisplayIndex } from '@/shared/api/reference'
import { didChangeState } from '@/shared/api/operationResult'
import { usePermissions } from '@/shared/auth/usePermissions'
import { csvMoney, type CsvColumn } from '@/shared/lib/download'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { OnlineBadge, SubscriberStateTag } from '@/shared/ui/StatusTag'
import { DataTable, type BulkAction } from '@/shared/ui/table/DataTable'
import { SubscriberFilterBar } from './SubscriberFilterBar'
import { RemainingTraffic } from './RemainingTraffic'
import {
  DELIVERY_METHODS,
  SUBSCRIBER_STATES,
  blockSubscriber,
  fetchSubscribers,
  type SubscriberRow,
} from './subscriberApi'

/** Ommaviy bloklash uchun qaysi holatlar mos — yopilgan/allaqachon bloklangan tanlanmaydi */
const BLOCKABLE_STATES = new Set(['ACTIVE', 'SUSPENDED'])

/** Abonentlar jadvali — saralash va filtrlar serverda (`subscriberListParams`). */
export function SubscriberTable() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const { selected: activeBranches, options: branchOptions } = useActiveBranches()
  const bulkBranch = useRef<string | null>(null)
  /*
    ⚠️ IKKI XARITA, VA BU ATAYLAB — bittasi ekranga, bittasi faylga.

    Ilgari bu yerda BITTA `usePlanIndex()` turardi va uni ham ustun
    (`render`), ham CSV eksporti (`exportColumns`) ishlatardi. Paket
    nomi tarjima qilinganda eksport ham JIMGINA tarjima bo'lardi —
    eksport esa mashina qayta o'qiydigan fayl (`QARORLAR.md` §30.39
    qoidasi: ekran — tarjima, hujjat va kalit — kanonik).
  */
  // ⚠️ `plan.read` YO'Q rol (AGENT, VENUE_MANAGER) uchun so'rov UMUMAN
  // yuborilmaydi — `useVoucherHolders` `org.node.read` bilan bir xil naqsh.
  // Usiz katalog so'rovi har sahifa ochilishida 403 berardi; ustun baribir
  // `?? row.activePlanId` bilan id ko'rsatadi, ya'ni ko'rinish o'zgarmaydi.
  const canReadPlans = can('plan.read')
  const planDisplay = usePlanDisplayIndex(canReadPlans)
  const planCanonical = useCanonicalPlanIndex(canReadPlans)

  const columns = useMemo<ColumnsType<SubscriberRow>>(
    () => [
      {
        key: 'login',
        title: t('subscribers.columns.username'),
        width: 170,
        sorter: true,
        render: (_, row) => <Typography.Text strong>{row.subscriber.login}</Typography.Text>,
      },
      {
        key: 'customerId',
        title: t('subscriber.profile.customerId'),
        width: 130,
        sorter: true,
        render: (_, row) => row.subscriber.profile?.customerId ?? '—',
      },
      {
        key: 'displayName',
        title: t('subscribers.columns.displayName'),
        width: 170,
        sorter: true,
        render: (_, row) => row.subscriber.displayName ?? '—',
      },
      {
        key: 'customerType',
        title: t('subscriber.profile.customerType'),
        sorter: true,
        width: 110,
        render: (_, row) => {
          const type = row.subscriber.profile?.customerType
          if (!type) return '—'
          return type === 'VIP' ? <Tag color="gold">VIP</Tag> : t(`subscriber.customerType.${type}`)
        },
      },
      {
        key: 'state',
        title: t('subscribers.columns.status'),
        width: 130,
        sorter: true,
        // Filtr qiymati SERVERGA yuboriladi. `filterMultiple: false` —
        // backend `state` ni YAKKA qiymat sifatida qabul qiladi
        // (`SubscriberQuery`), ko'p tanlash imkoni yolg'on va'da bo'lardi.
        filters: SUBSCRIBER_STATES.map((s) => ({ text: t(`subscriberState.${s}`), value: s })),
        filterMultiple: false,
        render: (_, row) => <SubscriberStateTag state={row.subscriber.state} />,
      },
      {
        key: 'branch',
        title: t('branch.label'),
        sorter: true,
        width: 170,
        render: (_, row) => {
          const branch = branchOptions.find((b) => b.id === row.subscriber.branchNodeId)
          return branch ? branch.name : row.subscriber.branchNodeId ? '…' : <Typography.Text type="secondary">{t('branch.none')}</Typography.Text>
        },
      },
      {
        key: 'online',
        title: t('status.onlineTitle'),
        sorter: true,
        width: 120,
        render: (_, row) => (row.online == null ? '—' : <OnlineBadge online={row.online} />),
      },
      {
        key: 'kind',
        title: t('subscribers.columns.kind'),
        sorter: true,
        width: 120,
        filters: DELIVERY_METHODS.map((k) => ({ text: t(`deliveryMethod.${k}`), value: k })),
        filterMultiple: false,
        render: (_, row) => t(`deliveryMethod.${row.subscriber.kind}`),
      },
      {
        key: 'plan',
        title: t('subscribers.columns.tariff'),
        sorter: true,
        width: 170,
        render: (_, row) =>
          row.activePlanId ? (planDisplay.get(row.activePlanId) ?? row.activePlanId) : '—',
      },
      {
        key: 'visp',
        title: t('subscriber.visp.column'),
        sorter: true,
        width: 150,
        render: (_, row) => row.subscriber.visp?.name ?? '—',
      },
      {
        key: 'agent',
        title: t('subscribers.columns.agent'),
        sorter: true,
        width: 190,
        /*
          ⚠️ BO'SH KATAK — NORMAL, XATO EMAS. Bugungi o'lchov: 74 ta
          abonentdan `agent_id` to'ldirilgani 0 ta, ya'ni ustun deyarli
          butunlay chiziqchadan iborat bo'ladi. Uni qizil qilish yoki
          «biriktirilmagan» deb ogohlantirish 74 ta soxta xato bo'lardi
          va operator ustunga qarashni birinchi kunidayoq to'xtatardi.
          Ikki haqiqiy sabab bor: abonentni operator to'g'ridan-to'g'ri
          sotgan bo'lishi mumkin, yoki zanjir hali to'ldirilmagan.

          ⚠️ Filtr YO'Q (voronkasiz): backend `agentId` ni QABUL QILADI,
          lekin tanlanadigan ro'yxat doira bo'yicha filtrlanishi kerak
          va u `SubscriberFilterBar` dagi `AgentSelect` da. Voronkaga
          agentlar ro'yxatini ikkinchi marta qurish ikkita haqiqat
          manbai bo'lardi.

          ⚠️ `AgentRef` uch holatni ALOHIDA chizadi: yo'q (chiziqcha),
          huquq yetmadi (qisqartirilgan id), topilmadi (sariq teg).
        */
        render: (_, row) => <AgentRef agentId={row.subscriber.agentId ?? null} showCode={false} />,
      },
      {
        key: 'balance',
        title: t('billing.moneyBalance'),
        sorter: true,
        width: 150,
        align: 'right',
        render: (_, row) =>
          // ⚠️ `null` = NOMA'LUM, nol emas. Ledger javob bermaganda ilgari
          // bu yerda "0 so'm" chiqardi va butun ro'yxat qarzdorlarga
          // aylanardi — ishonarli ko'rinishda, chunki nol ham haqiqiy
          // balans. Endi savol belgisi bilan "—" chiqadi.
          row.balance === null ? (
            <Tooltip title={t('billing.balanceUnknownHint')}>
              <Typography.Text type="secondary">—</Typography.Text>
            </Tooltip>
          ) : (
            // Manfiy balans qizil: bu "to'lov kerak" signali va ro'yxatni
            // ko'zdan kechirganda darhol ko'rinishi kerak
            <Typography.Text type={row.balance.amount < 0 ? 'danger' : undefined}>
              {formatMoneyValue(row.balance)}
            </Typography.Text>
          ),
      },
      {
        key: 'remaining',
        sorter: true,
        // ⚠️ Bu ustun JORIY grantning qoldig'i (`SubscriberPage` javadoc'i),
        // abonent kartochkasidagi "Jami qolgan trafik" esa YIG'INDI. Ikki
        // son har xil chiqadi va izohsiz bu nuqsonga o'xshaydi.
        title: (
          <Tooltip title={t('subscribers.columns.remainingHint')}>
            <span>{t('subscribers.columns.remaining')}</span>
          </Tooltip>
        ),
        width: 140,
        align: 'right',
        render: (_, row) => (
          // ⚠️ Backendda `remainingBytes = null` IKKI xil holatni bildiradi:
          // "cheksiz" va "grant yo'q" (`SubscriberPage` javadoc'i). Ularni
          // `activeGrantId` ajratadi — bittasini ikkinchisi deb ko'rsatish
          // operatorga qarama-qarshi xulosa berardi. Va ikkalasi ham
          // "0 GB" EMAS: hozir jonli tizimdagi abonentlarning hammasi
          // kvotasiz va ular "tugagan" bo'lib ko'rinardi.
          <RemainingTraffic
            hasGrant={row.activeGrantId !== null}
            remainingBytes={row.remainingBytes}
            compact
          />
        ),
      },
      {
        key: 'expiresAt',
        title: t('subscribers.columns.expiresAt'),
        width: 180,
        sorter: true,
        render: (_, row) =>
          row.expiresAt ? (
            // Vaqtlar bazada UTC, ekranda lokal zonada. Tooltip'da ikkalasi —
            // sheriklar UTC+4:30 va UTC+5 da ishlaydi va "qaysi vaqt bu?"
            // savoli doim chiqadi
            <Tooltip title={utcTooltip(row.expiresAt)}>{formatDateTime(row.expiresAt)}</Tooltip>
          ) : (
            '—'
          ),
      },
    ],
    [t, planDisplay, branchOptions],
  )

  // CSV — faqat EKRANDAGI (joriy sahifa) qatorlar.
  //
  // ⚠️ Bu yo'l ATAYLAB qoldirildi: operator ko'pincha ko'rib turgan
  // sahifasini tez nusxa qilmoqchi bo'ladi va buning uchun serverga
  // borish keraksiz. To'liq reyestr esa endi SERVERDA yasaladi
  // (`GET /api/reports/export/subscribers`) va u «Hisobotlar»
  // sahifasidagi «Batafsil eksport» da — havola jadval ostida turadi,
  // aks holda operator "to'liq eksport yo'q ekan" degan xulosaga
  // kelardi.
  //
  // ⚠️ Nega server eksporti AYNAN SHU jadvalga qo'yilmadi: reyestr
  // endpointining sana oynasi `created_at` ga qo'llanadi ("shu davrda
  // RO'YXATGA OLINGANLAR") va oyna eng ko'pi 366 kun. Bu jadvalda sana
  // tanlagichi yo'q, ya'ni tugma jimgina "oxirgi 30 kunda ro'yxatga
  // olinganlar" ni berardi va operator uni butun ro'yxat deb o'qirdi.
  const exportColumns = useMemo<readonly CsvColumn<SubscriberRow>[]>(
    () => [
      { header: t('subscribers.columns.username'), value: (row) => row.subscriber.login },
      { header: t('subscribers.columns.status'), value: (row) => row.subscriber.state },
      { header: t('subscribers.columns.tariff'), value: (row) => (row.activePlanId ? (planCanonical.get(row.activePlanId) ?? row.activePlanId) : '') },
      // ⚠️ `row.balance.amount` XOM minor son (2500000). Ekrandagi ustun
      // esa `formatMoneyValue` bilan "25 000 afg'oni" ko'rsatadi — ya'ni
      // bir xil sarlavha ostida faylda 100 barobar boshqa kattalik
      // ketardi. `csvMoney` ikkalasini bitta o'lchovga keltiradi va
      // shakli backend hisobot eksporti bilan aynan bir xil.
      // ⚠️ Noma'lum balans faylda BO'SH katak bo'ladi, "0" emas: eksport
      // ekrandagidan ko'ra ko'proq yashaydi va nol bo'lib ketgan yolg'on
      // raqam keyin hisob-kitobga qo'shilib ketishi mumkin.
      { header: t('billing.moneyBalance'), value: (row) => (row.balance ? csvMoney(row.balance) : '') },
      // Valyuta ALOHIDA ustun: summa katagi toza son bo'lib qolsin, aks
      // holda Excel ustunni yig'a olmaydi.
      { header: t('tenants.currency'), value: (row) => row.balance?.currency ?? '' },
      { header: t('subscribers.columns.expiresAt'), value: (row) => row.expiresAt ?? '' },
    ],
    [t, planCanonical],
  )

  /**
   * Ommaviy bloklash — 3.2-bo'lim (CLAUDE.md) qoidasi HAR BIR abonentga
   * alohida qo'llanadi: baza avval, keyin CoA/Disconnect, xatoda rollback
   * yo'q. Shuning uchun bu yerda ham har bittasi MUSTAQIL so'rov — bittasi
   * FAILED bo'lsa qolganlari baribir davom etadi, natija esa YIG'MA holda
   * ko'rsatiladi (nechtasi qo'llandi, nechtasi yo'q).
   */
  const bulkActions = useMemo<readonly BulkAction<SubscriberRow>[]>(() => {
    const actions: BulkAction<SubscriberRow>[] = []
    if (can('subscriber.update') && branchOptions.length > 0) {
      actions.push({
        key: 'branch',
        label: t('branch.assignBulk'),
        confirmTitle: (count) => t('branch.assignBulkTitle', { count }),
        confirmContent: () => (
          <Select<string>
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder={t('branch.choose')}
            onChange={(value) => {
              bulkBranch.current = value
            }}
            options={branchOptions.map((b) => ({ value: b.id, label: branchLabel(b) }))}
          />
        ),
        onConfirm: async (_keys, rows) => {
          const branch = bulkBranch.current
          if (!branch) {
            void message.error(t('branch.chooseRequired'))
            return
          }
          try {
            const count = await setSubscribersBranch(rows.map((row) => row.subscriber.id), branch)
            void message.success(t('branch.assignBulkDone', { count }))
          } catch (error) {
            void message.error(translateError(error, t))
          } finally {
            bulkBranch.current = null
            void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
          }
        },
      })
    }
    if (!can('subscriber.block')) return actions
    return [
      ...actions,
      {
        key: 'block',
        label: t('actions.block'),
        danger: true,
        confirmTitle: (count) => t('actions.blockBulkTitle', { count }),
        confirmContent: (count) => t('actions.blockBulkConfirm', { count }),
        onConfirm: async (_keys, rows) => {
          const results = await Promise.allSettled(rows.map((row) => blockSubscriber(row.subscriber.id)))
          const ok = results.filter((r) => r.status === 'fulfilled' && didChangeState(r.value)).length
          const failed = results.length - ok
          if (failed === 0) void message.success(t('actions.blockBulkDone', { count: ok }))
          else void message.warning(t('actions.blockBulkPartial', { ok, failed }))
          void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
        },
      },
    ]
  }, [can, t, message, queryClient, branchOptions])

  return (
    <DataTable<SubscriberRow>
      queryKey={[...queryKeys.subscribers.all, 'branches', activeBranches.join(',')]}
      fetchPage={(state, signal) => fetchSubscribers(state, signal, activeBranches)}
      columns={columns}
      rowKey={(row) => row.subscriber.id}
      tableId="subscribers"
      stickyFirstColumn
      searchable
      searchPlaceholder={t('subscribers.searchPlaceholder')}
      // ⚠️ Filtrlar KO'RINADIGAN joyga chiqarildi.
      //
      // Ular ilgari ham bor edi — jadval sarlavhasidagi kichik voronka
      // ikonkasi ichida. Texnik jihatdan ishlardi, amalda esa yo'q edi:
      // egasi "filtrlar yo'q" deb xabar qildi. Ko'rinmagan boshqaruv
      // mavjud emas bilan barobar.
      //
      // Voronkalar OLIB TASHLANMADI: ikkalasi bir xil URL holatidan
      // o'qiydi va yozadi, ya'ni zid bo'lolmaydi. Odat qilgan
      // foydalanuvchi eskisini ishlatishda davom etadi.
      toolbarExtra={<SubscriberFilterBar />}
      scrollX={1490}
      exportColumns={exportColumns}
      exportFilename="abonentlar"
      exportNoteExtra={t('dataTable.exportServerHint')}
      bulkActions={bulkActions}
      isRowSelectable={(row) => BLOCKABLE_STATES.has(row.subscriber.state)}
      onRow={(row) => ({
        style: { cursor: 'pointer' },
        onClick: () => navigate(`/subscribers/${row.subscriber.id}`),
      })}
    />
  )
}
